import axios, {
  type AxiosError,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
  CanceledError,
} from "axios";
import { addBreadcrumb, BREADCRUMB_TYPES } from "./breadcrumbs";
import { reportClientError } from "./errorReporter";
import {
  notifyNetworkError,
  notifyServerDown,
  notifyServerOk,
  notifyQueued,
} from "@/components/ConnectivityBanner";
import { tryServeFromCache } from "./offline-cache";
import { isQueueable, queueRequest } from "./offline-outbox";

// Módulo augmentation: campos custom que Altiv inyecta en cada request.
// Sin esto, TS marca `_startedAt`/`_retryCount`/`_skipOfflineFallback` como
// errores en cualquier file que migre a TS.
declare module "axios" {
  export interface InternalAxiosRequestConfig {
    _startedAt?: number;
    _retryCount?: number;
    _skipOfflineFallback?: boolean;
    _skipNotifyOnFail?: boolean;
  }
  export interface AxiosRequestConfig {
    _skipOfflineFallback?: boolean;
    _skipNotifyOnFail?: boolean;
  }
}

const API = `${import.meta.env.VITE_BACKEND_URL}/api`;

const MAX_RETRIES = 3;
const RETRY_DELAY = 1000;

const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

const api = axios.create({
  baseURL: API,
  timeout: 30000,
});

api.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  const token = localStorage.getItem("altiv_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  config._startedAt = Date.now();

  // OFFLINE WRITE INTERCEPT — short-circuit queueable mutations cuando no hay red.
  const isWrite = ["post", "put", "patch", "delete"].includes(
    (config.method || "get").toLowerCase()
  );
  const offline = typeof navigator !== "undefined" && navigator.onLine === false;
  if (
    offline &&
    isWrite &&
    !config._skipOfflineFallback &&
    // isQueueable now also rejects FormData/Blob payloads (receipts, doc
    // uploads): IDB structured-clone can't store them, so the old code path
    // would have created a "queued" toast over silently-lost data.
    isQueueable(config.method, config.url, config.data)
  ) {
    const summary = _summaryForRequest(config);
    // Bind the queued entry to the user who created it — flushOutbox drops
    // entries whose user_id doesn't match the user logged in at replay time
    // so a previous tenant's queued writes can't land under a new tenant's
    // bearer token on a shared device.
    let userId: string | null = null;
    try {
      const raw = localStorage.getItem("altiv_user_cache");
      if (raw) userId = JSON.parse(raw)?.id ?? null;
    } catch {}
    await queueRequest({
      method: config.method,
      url: config.url,
      data: config.data,
      headers: { ...(config.headers || {}) },
      summary,
      user_id: userId,
    });
    try {
      notifyQueued(summary);
    } catch {}
    config.adapter = async () => ({
      data: { queued: true, offline: true },
      status: 202,
      statusText: "Accepted (queued offline)",
      headers: {},
      config,
      request: null,
    });
  }
  return config;
});

function _summaryForRequest(config: InternalAxiosRequestConfig): string {
  const method = (config.method || "GET").toUpperCase();
  const url = config.url || "";
  if (url.startsWith("/expenses") && method === "POST") {
    const data = config.data as
      | { amount?: number; currency?: string; description?: string }
      | undefined;
    const amt = data?.amount;
    const cur = data?.currency || "USD";
    const desc = data?.description || "Gasto";
    return `${desc} — ${amt ?? "?"} ${cur}`;
  }
  if (/^\/legs\//.test(url) && method === "PUT") {
    return `Edición de tramo`;
  }
  if (/^\/legs\/[^/?]+\/parameters(\?|$)/.test(url) && method === "POST") {
    return `Parámetros de vuelo`;
  }
  if (/^\/legs\/[^/?]+\/post-flight(\?|$)/.test(url) && method === "POST") {
    return `Bitácora de vuelo`;
  }
  return `${method} ${url}`;
}

api.interceptors.response.use(
  (res: AxiosResponse) => {
    try {
      const dur = res.config._startedAt ? `${Date.now() - res.config._startedAt}ms` : "";
      const method = (res.config.method || "get").toUpperCase();
      addBreadcrumb(BREADCRUMB_TYPES.API, `${method} ${res.config.url} ${res.status} ${dur}`);
    } catch {}
    try {
      const fromCache = res.headers?.["x-altiv-source"] === "offline-cache";
      if (!fromCache) notifyServerOk();
    } catch {}
    return res;
  },
  async (err: AxiosError) => {
    const config = err.config as InternalAxiosRequestConfig | undefined;
    if (!config) {
      return Promise.reject(err);
    }

    // ── CANCELLED REQUESTS ARE NOT FAILURES ──────────────────────────────
    // React Query, AbortController, component unmounts, route changes, and
    // StrictMode double-mounts all cancel in-flight requests intentionally.
    // Treating cancellation as a server outage produces false "Network error"
    // banners on perfectly healthy backends (real prod regression observed
    // when navigating into the CBP tab — see commit message for repro).
    // Short-circuit before retry/banner logic.
    if (axios.isCancel(err) || err instanceof CanceledError || err.code === "ERR_CANCELED") {
      return Promise.reject(err);
    }

    config._retryCount = config._retryCount || 0;

    const shouldRetry =
      config._retryCount < MAX_RETRIES &&
      (!err.response ||
        err.response?.status === 503 ||
        err.response?.status === 502 ||
        err.response?.status === 504);

    if (shouldRetry) {
      config._retryCount += 1;
      try {
        if (typeof navigator !== "undefined" && navigator.onLine === false) {
          notifyNetworkError();
        }
      } catch {}

      await sleep(RETRY_DELAY * config._retryCount);

      return api(config);
    }

    if (!err.response) {
      // OFFLINE READ INTERCEPT — intentar servir desde snapshot offline antes de fallar.
      try {
        const isGet = (config.method || "get").toLowerCase() === "get";
        if (isGet && !config._skipOfflineFallback) {
          const cached = await tryServeFromCache({
            method: config.method,
            url: config.url,
          });
          if (cached !== null && cached !== undefined) {
            return {
              data: cached,
              status: 200,
              statusText: "OK (offline cache)",
              headers: { "x-altiv-source": "offline-cache" },
              config,
              request: null,
            };
          }
        }
      } catch {}

      // Telemetry / fire-and-forget calls (web-vitals, analytics) opt out:
      // a blocked ad-network rule or extension shouldn't paint a "server down"
      // banner for the user — the request is non-critical by design.
      if (!config._skipNotifyOnFail) {
        console.error("Network error - server may be unavailable");
        try {
          if (typeof navigator !== "undefined" && navigator.onLine !== false) {
            notifyServerDown();
          }
        } catch {}
      }
      return Promise.reject({
        ...err,
        message: "Unable to connect to server. Please check your connection and try again.",
        isNetworkError: true,
      });
    }

    // Fire-and-forget telemetry (web-vitals etc.) opts out of logout-on-401.
    // A transient 401 on analytics shouldn't tear down the user's session —
    // it would cascade: token wiped → next real call also 401 → no-response
    // banner → user thinks the backend died.
    if (err.response?.status === 401 && !config._skipNotifyOnFail) {
      localStorage.removeItem("altiv_token");
      // NOTE: we deliberately do NOT remove "altiv_trusted_device" here. A 401
      // means the SESSION (bearer) is invalid — it says nothing about whether
      // this device is still trusted. The trusted-device token is a 30-day
      // proof that lets the user skip 2FA on return; wiping it on every session
      // expiry / transient 401 silently defeated "remember this device" and
      // forced 2FA on essentially every login (observed in prod: users
      // re-minting a fresh trusted device on each sign-in). It only dies on
      // explicit user revocation (Settings) or its own 30-day expiry.
      // Forced logout bypasses AuthContext.logout — purge the per-origin
      // offline IDB ourselves so the next user on this device can't see
      // the expired user's snapshot or replay their queued writes.
      import("./offline-db")
        .then(({ clearSnapshot, clearOutbox }) =>
          Promise.allSettled([clearSnapshot(), clearOutbox()])
        )
        .catch(() => {});
      if (!window.location.pathname.includes("/login") && window.location.pathname !== "/") {
        window.location.href = "/";
      }
    }

    if (err.response?.status === 503) {
      console.error("Service temporarily unavailable after retries");
      return Promise.reject({
        ...err,
        message: "Service temporarily unavailable. Please try again in a moment.",
        isServiceError: true,
      });
    }

    try {
      const dur = config._startedAt ? `${Date.now() - config._startedAt}ms` : "";
      const method = (config.method || "get").toUpperCase();
      const status = err.response?.status || "network";
      addBreadcrumb(BREADCRUMB_TYPES.API, `${method} ${config.url} ${status} ${dur}`);
    } catch {}

    try {
      const status = err.response?.status;
      const isServerError = status && status >= 500;
      const isNetworkError = !err.response;
      const isReportEndpoint = (config.url || "").includes("/client-errors");
      if (!isReportEndpoint && (isServerError || isNetworkError)) {
        reportClientError({
          kind: isServerError ? "frontend_api_5xx" : "frontend_api_network",
          severity: "error",
          message: `${(config.method || "get").toUpperCase()} ${config.url} → ${status || "network error"}: ${err.message || ""}`,
          stack: err.stack || "",
          extra: {
            status: status || null,
            method: config.method,
            url: config.url,
          },
        });
      }
    } catch {}

    return Promise.reject(err);
  }
);

export interface ApiHealthResponse {
  status: string;
  error?: string;
  [key: string]: unknown;
}

export const checkApiHealth = async (retries = 3): Promise<ApiHealthResponse> => {
  for (let i = 0; i < retries; i++) {
    try {
      const response = await axios.get<ApiHealthResponse>(`${API}/health`, {
        timeout: 5000,
      });
      return response.data;
    } catch {
      if (i < retries - 1) {
        await sleep(1000 * (i + 1));
      }
    }
  }
  return { status: "unreachable", error: "Health check failed after retries" };
};

export async function withRetry<T>(operation: () => Promise<T>, maxRetries = 3): Promise<T> {
  let lastError: unknown;
  for (let i = 0; i < maxRetries; i++) {
    try {
      return await operation();
    } catch (err) {
      lastError = err;
      const e = err as { isNetworkError?: boolean; isServiceError?: boolean };
      if (i < maxRetries - 1 && (e.isNetworkError || e.isServiceError)) {
        await sleep(1000 * (i + 1));
      }
    }
  }
  throw lastError;
}

export default api;
